import { DataTypeName } from ".";

const baseGenericPrefixes = { Array: "Multiple", Optional: "Maybe" } as const;

type BaseGenericPrefixes = typeof baseGenericPrefixes;

/**
 * Defines a simple grammar for generic datatypes for use in email flows that is both human-readable and machine-parseable.
 * Full type safety and intellisense are supported up to the specified recursion depth limit.
 * Probably no more than 3 levels of recursion will ever be needed.
 *
 * @remarks
 *
 * Arrays are not allowed to have optional elements, but the array itself can be optional.
 * Optional types cannot themselves be optional.
 *
 * In BNF:
 * <GenericDataType> ::= <DataTypeName> | <ArrayDataType> | <OptionalDataType>
 * <ArrayDataType> ::= "Multiple" <RequiredDataType>
 * <RequiredDataType> ::= <DataTypeName> | <ArrayDataType>
 * <OptionalDataType> ::= "Maybe" <DataTypeName> | <ArrayDataType>
 */
export type GenericPrefixes = {
  [K in keyof BaseGenericPrefixes]: `${BaseGenericPrefixes[K]} `;
};

export const genericPrefixes: GenericPrefixes = {
  Array: `${baseGenericPrefixes.Array} `,
  Optional: `${baseGenericPrefixes.Optional} `,
};

export type GenericDataType<TDepth = 1> = TDepth extends RecursionDepths[number]
  ? DataTypeName | ArrayDataType<TDepth> | OptionalDataType<TDepth>
  : never;

type ArrayDataType<TDepth> = TDepth extends RecursionDepths[number]
  ? `${GenericPrefixes["Array"]}${RequiredDataType<Inc<TDepth>>}`
  : never;

type RequiredDataType<TDepth> = TDepth extends RecursionDepths[number]
  ? Exclude<
      GenericDataType<TDepth>,
      `${GenericPrefixes["Optional"]}${DataTypeName | ArrayDataType<TDepth>}`
    >
  : never;

type OptionalDataType<TDepth> = TDepth extends RecursionDepths[number]
  ? `${GenericPrefixes["Optional"]}${DataTypeName | ArrayDataType<TDepth>}`
  : never;

export type RecursionDepths = [1, 2, 3];
export type Inc<T> = T extends RecursionDepths[number]
  ? RecursionDepths[T]
  : never;

export type ExtractArrayType<T> =
  T extends `${GenericPrefixes["Array"]}${infer U}` ? U : never;

export type ExtractOptionalType<T> =
  T extends `${GenericPrefixes["Optional"]}${infer U}` ? U : never;

type AnyGenericPrefix = GenericPrefixes[keyof GenericPrefixes];

/** The underlying type of `T`, with every generic prefix stripped. */
export type BaseDataType<T> = T extends `${AnyGenericPrefix}${infer U}`
  ? BaseDataType<U>
  : T;

/**
 * Every `dataType` whose underlying type is `T`, however it is decorated:
 * `GenericsOf<"Text">` is `"Text" | "Maybe Text" | "Multiple Text" | ...`.
 */
export type GenericsOf<T extends DataTypeName> = Extract<
  GenericDataType,
  T | `${string} ${T}`
>;

export function removeGenericFromType(type: GenericDataType): DataTypeName {
  const prefix = Object.values(genericPrefixes).find((prefix) =>
    type.startsWith(prefix),
  );
  return prefix
    ? // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      removeGenericFromType(type.slice(prefix.length) as GenericDataType)
    : // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      (type as DataTypeName);
}
